/**
 * THE MOVE — what runs on Erez's Mac, what runs on the Tailormind box, and
 * where the client work actually lands. Built 2026-08-11, the night the desk
 * first came up on the server.
 *
 * The question this answers: "if we move right now, what changes?"
 *
 * The answer the picture is arranged to make unmissable — LESS THAN YOU THINK.
 * Client work has never happened on the Mac. An agent SSHes into the client's
 * own box, edits the code there and pushes to GitHub from the box. Moving the
 * agents changes WHERE THEY SSH FROM and nothing else. That is why the fourth
 * column exists: without it the diagram looks like a migration, and it is not.
 *
 * A deployment view on the house engine (`./c4`) — so ports are part of the
 * names, every wire carries a protocol, and red is spent only on the three
 * things that are genuinely unfinished:
 *
 *   1 · VOICE   — voice-say.sh POSTs to localhost:3001 ON THE MAC. Every agent
 *                 on the box is mute until that is tunnelled back.
 *   2 · SECRETS — all 80 integration files were copied to an internet-facing
 *                 box, including client AWS credentials that it does not need.
 *   3 · AIR DUCT— /opt/airduct* is not a git repository and has no remote.
 *                 If that box dies the demo is gone. Verified, not assumed.
 *
 * Nothing has a hand-written width: a leaf measures its own text, a container
 * measures its children, and every wire resolves against the placed rects.
 */
import { Canvas, leaf, person, box, type Diagram } from "./c4";

const MOVE: Diagram = (() => {
  const erez = box("mv-act", undefined, "col", [person("mv-e", "Erez")], { pad: 0 });

  // ── WHAT CANNOT LEAVE THE MAC ──────────────────────────────────────────────
  // Two reasons only, and both are about hardware or identity: speakers he can
  // hear, and a browser that is already logged into his accounts. Everything
  // else on this column is there by habit, not by necessity.
  const mac = box("mv-mac", "EREZ'S MAC — stays local", "col", [
    box("mv-voice", "Voice · his speakers", "row", [
      leaf("mv-kok", "Kokoro :8787", "[voice-engine]", "AudioLines"),
      leaf("mv-vapi", "voice-api :3001", "[My Jarvis Voice]", "Volume2"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("mv-ident", "His identity · cannot be copied", "row", [
      leaf("mv-chrome", "Chrome + logins", "[claude-in-chrome]", "Globe"),
      leaf("mv-vite", "Vite :5173", "[WorkOS redirect]", "MonitorSmartphone"),
    ], { depth: 2, gap: 10, pad: 10 }),
    leaf("mv-jl", "Jarvis — the backup", "[one tmux pane]", "LifeBuoy"),
    leaf("mv-tg", "telegram-bridge", "[MOVES — one poller only]", "Send", "gate"),
  ], { gap: 12 });

  // ── THE BOX ────────────────────────────────────────────────────────────────
  // 25 MB, not 72 GB: identity, laws and tools. No project repos, no dashboard,
  // no client work. The brain and the body without the filing cabinet.
  const server = box("mv-srv", "TAILORMIND BOX — 178.105.196.150 · cx33", "col", [
    box("mv-desk", "The desk", "row", [
      leaf("mv-tmux", "tmux desk", "[prefix C-a]", "LayoutGrid"),
      leaf("mv-heads", "Jarvis + 9 heads", "[user tm]", "Users"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("mv-brain", "The brain — 25 MB", "row", [
      leaf("mv-laws", "CLAUDE.md", "[the laws]", "Scale"),
      leaf("mv-id", "agents/*/CLAUDE.md", "[identity]", "Fingerprint"),
      leaf("mv-hooks", ".claude/hooks", "[bun]", "Webhook"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("mv-tools", "Tools", "row", [
      leaf("mv-pw", "Playwright", "[headless chromium]", "Camera"),
      leaf("mv-keys", "integrations · 80 keys", "[TOO MANY]", "KeyRound", "gate"),
    ], { depth: 2, gap: 10, pad: 10 }),
    leaf("mv-omni", "omnigent — stopped", "[/opt/omnigent · kept]", "PowerOff"),
    leaf("mv-mute", "NO VOICE PATH", "[agents are mute]", "VolumeX", "gate"),
  ], { gap: 12 });

  // ── WHERE THE WORK ACTUALLY IS ─────────────────────────────────────────────
  // The column that makes the move look small. None of this ever lived on the
  // Mac. Git state checked on every box on 2026-08-11, not taken on report.
  const clients = box("mv-cli", "CLIENT BOXES — the work has always been here", "col", [
    leaf("mv-fal", "falcon-agent", "[2.28.5.68 · git clean]", "Building2"),
    leaf("mv-out", "outform", "[178.105.74.255 · git clean]", "Factory"),
    leaf("mv-boil", "boiling · AWS", "[SSM tunnel]", "Flame"),
    leaf("mv-air", "airduct-demo", "[NO GIT · NO REMOTE]", "AlertTriangle", "gate"),
  ], { gap: 12 });

  // ── SHARED BY BOTH SIDES ───────────────────────────────────────────────────
  // Neither the Mac nor the box owns any of this, which is exactly why the move
  // is cheap: the durable state was never on either machine.
  const cloud = box("mv-cloud", "CLOUD — owned by neither machine", "col", [
    leaf("mv-gh", "GitHub · erezgit", "[the code]", "GitBranch"),
    leaf("mv-neon", "Neon Postgres", "[memories · KB]", "Database"),
    leaf("mv-cf", "Cloudflare Pages", "[erez.dashboard]", "Cloud"),
    leaf("mv-anth", "Anthropic", "[the model]", "Sparkles"),
  ], { gap: 12 });

  return {
    key: "mv",
    title: "The move — local vs the server",
    subtitle: "What changes, what cannot, and the three things still open · 2026-08-11",
    padBottom: 96,
    cols: [
      { root: erez, align: "center" },
      { root: mac, align: "center" },
      { root: server, align: "center" },
      { root: clients, align: "center" },
      { root: cloud, align: "center" },
    ],
    wires: [
      // SHORT labels and container-level anchors, both deliberate. c4 paints the
      // wire SVG UNDER the boxes, so any wire whose path crosses a container is
      // hidden by that container's fill, and any label whose midpoint lands on a
      // box gets clipped mid-word. The first draft of this diagram did both:
      // "SSH — unchanged by the move" rendered as "the move". The argument moved
      // to the legend, where there is room for a sentence.
      { a: "mv-e", b: "mv-mac", as: "r", bs: "l", label: "tmux" },
      { a: "mv-mac", b: "mv-srv", as: "r", bs: "l", label: "SSH" },
      // THE WORK LOOP — the point of the whole picture.
      { a: "mv-srv", b: "mv-cli", as: "r", bs: "l", label: "SSH" },
      { a: "mv-cli", b: "mv-cloud", as: "r", bs: "l", label: "git push" },
      // Over the top, so it clears every container between them.
      { a: "mv-srv", b: "mv-cloud", as: "t", bs: "t", label: "SQL · HTTPS" },
      // The two open wounds, routed UNDER everything for the same reason — and
      // deliberately UNLABELLED. Both midpoints land on a container edge, so any
      // label clips; the red legend line names both, which is where a sentence
      // belongs anyway.
      { a: "mv-mute", b: "mv-mac", as: "b", bs: "b", red: true },
      { a: "mv-air", b: "mv-cloud", as: "b", bs: "b", red: true },
    ],
    legend: [
      { style: "solid", label: "works today — and the SSH to the client boxes is UNCHANGED by the move" },
      { style: "red", label: "open: voice is mute · 80 keys shipped where ~10 are needed · Air Duct is not in git" },
    ],
  };
})();

/** What runs where, and the three things still open before the move completes. */
export function TheMoveDiagram() {
  return <Canvas d={MOVE} mode="contrast" />;
}
